import { useEffect, useRef, useState } from 'react';
import { BYBIT, fetchLinearTickers, resolveBybitSymbol } from './bybit';
import { calcRSI, calcEMA } from './indicators';

/* ══════════════════════════════════════════════════════════════════════════
   Real RSI14 from Bybit linear klines
   ─────────────────────────────────────────────────────────────────────────
   - Wilder RSI (utils/indicators.js calcRSI), CLOSED candles only: the last
     candle is dropped while it is still open (start + interval > server time).
   - At least 250 closed candles, otherwise no value (shown as "kevés adat").
   - Rate limit: Bybit allows 600 requests / 5 s / IP. Requests go through one
     queue with a small concurrency, results are cached per symbol+interval in
     memory and localStorage (1D/4h: 15 min, 1h: 5 min, 15m: 2 min).
   ══════════════════════════════════════════════════════════════════════════ */

export const RSI_PERIOD = 14;
const MIN_CLOSED = 250;
const KLINE_LIMIT = 300;
const CONCURRENCY = 6;

export const TF = {
  '15':  { label: '15m', ms: 15 * 60e3,     ttl: 2 * 60e3 },
  '60':  { label: '1h',  ms: 60 * 60e3,     ttl: 5 * 60e3 },
  '240': { label: '4h',  ms: 4 * 60 * 60e3, ttl: 15 * 60e3 },
  'D':   { label: '1D',  ms: 24 * 60 * 60e3, ttl: 15 * 60e3 },
};

const LS_KEY = 'rsi_cache_v1';
let mem = (() => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
})();

let saveTimer = null;
function persist() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    // Drop entries older than a day so the object does not grow forever.
    const cutoff = Date.now() - 24 * 3600e3;
    for (const k of Object.keys(mem)) if (mem[k].fetchedAt < cutoff) delete mem[k];
    try { localStorage.setItem(LS_KEY, JSON.stringify(mem)); } catch (_) {}
  }, 500);
}

// ── Request queue ─────────────────────────────────────────────────────────────
const queue = [];
let running = 0;
function runTask({ fn, resolve, reject }) {
  running++;
  fn().then(resolve, reject).finally(() => { running--; pump(); });
}
function pump() {
  while (running < CONCURRENCY && queue.length) runTask(queue.shift());
}
function enqueue(fn) {
  return new Promise((resolve, reject) => { queue.push({ fn, resolve, reject }); pump(); });
}

const inflight = {};

async function loadKlines(symbol, interval) {
  const url = `${BYBIT}/market/kline?category=linear&symbol=${symbol}&interval=${interval}&limit=${KLINE_LIMIT}`;
  const res = await fetch(url);
  if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
  const json = await res.json();
  if (json.retCode !== 0) throw new Error(json.retMsg || 'Bybit hiba');
  const serverTime = Number(json.time) || Date.now();
  // Bybit returns newest first: [start, open, high, low, close, volume, turnover]
  const rows = (json.result?.list ?? []).slice().reverse();
  const closed = rows.filter(r => Number(r[0]) + TF[interval].ms <= serverTime);
  return closed;
}

function compute(closed) {
  const closes = closed.map(r => parseFloat(r[4]));
  if (closes.length < MIN_CLOSED) return { rsi: null, reason: 'short', candles: closes.length };
  const ema50 = calcEMA(closes, 50);
  const last = closes[closes.length - 1];
  return {
    rsi: calcRSI(closes, RSI_PERIOD),
    candles: closes.length,
    lastClose: last,
    aboveEma50: ema50 != null ? last > ema50 : null,
    candleStart: Number(closed[closed.length - 1][0]),
  };
}

/** Cached entry (any age) or null. */
export function peekRSI(symbol, interval) {
  return mem[`${symbol}|${interval}`] || null;
}

/** Resolves to { rsi, candles, lastClose, aboveEma50, candleStart, fetchedAt, stale }. */
export function fetchRSI(symbol, interval) {
  const key = `${symbol}|${interval}`;
  const cached = mem[key];
  if (cached && !cached.stale && Date.now() - cached.fetchedAt < TF[interval].ttl) return Promise.resolve(cached);
  if (inflight[key]) return inflight[key];
  inflight[key] = enqueue(() => loadKlines(symbol, interval))
    .then(closed => {
      const entry = { ...compute(closed), fetchedAt: Date.now(), stale: false };
      mem[key] = entry;
      persist();
      return entry;
    })
    .catch(() => {
      // Network/limit failure: keep showing the last known value, marked stale.
      // With a cached value: show it, marked stale (a real cache). Without one:
      // an error entry with NO fetchedAt, so footers never call it "gyorsítótár".
      if (cached) return { ...cached, stale: true };
      return { rsi: null, reason: 'error' };
    })
    .finally(() => { delete inflight[key]; });
  return inflight[key];
}

/* ── React hook ────────────────────────────────────────────────────────────
   coins: CoinGecko market rows (need id, symbol, current_price).
   Returns { rows: { [coinId]: entry & { symbol } }, symbols, pending, meta }.
   Results are flushed to state in batches so a 100-coin scan does not cause
   100 renders. */
export function useCoinRSI(coins, interval, { enabled = true } = {}) {
  const [symbols, setSymbols] = useState(null);   // { [coinId]: bybitSymbol|null }
  const [tickersFailed, setTickersFailed] = useState(false);
  const [rows, setRows] = useState({});
  const [pending, setPending] = useState(0);
  const [tick, setTick] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const buf = useRef({});
  const flushTimer = useRef(null);

  const idsKey = (coins ?? []).map(c => c.id).join(',');

  useEffect(() => {
    if (!enabled || !coins?.length) return;
    let alive = true;
    fetchLinearTickers(5 * 60 * 1000)
      .then(map => {
        if (!alive) return;
        const s = {};
        for (const c of coins) s[c.id] = resolveBybitSymbol(c.symbol, c.current_price, map);
        setSymbols(s);
        setTickersFailed(false);
      })
      .catch(() => { if (alive) setTickersFailed(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, enabled, retryNonce]);

  // Re-check when the cache TTL of this interval runs out.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setTick(t => t + 1), TF[interval].ttl);
    return () => clearInterval(id);
  }, [interval, enabled]);

  useEffect(() => {
    if (!enabled || !symbols) return;
    let alive = true;
    const flush = () => {
      flushTimer.current = null;
      if (!alive) return;
      const b = buf.current; buf.current = {};
      setRows(prev => ({ ...prev, ...b }));
    };
    const initial = {};
    const todo = [];
    for (const [id, sym] of Object.entries(symbols)) {
      if (!sym) continue;
      const c = peekRSI(sym, interval);
      if (c) initial[id] = { ...c, symbol: sym };
      todo.push([id, sym]);
    }
    setRows(initial);
    setPending(todo.length);
    todo.forEach(([id, sym]) => {
      fetchRSI(sym, interval).then(entry => {
        if (!alive) return;
        buf.current[id] = { ...entry, symbol: sym };
        setPending(p => p - 1);
        if (!flushTimer.current) flushTimer.current = setTimeout(flush, 250);
      });
    });
    return () => { alive = false; clearTimeout(flushTimer.current); flushTimer.current = null; };
  }, [symbols, interval, enabled, tick, retryNonce]);

  // Only real data (fresh or cached) feeds the footer; error entries have no fetchedAt.
  const entries = Object.values(rows).filter(e => e.fetchedAt);
  const meta = entries.length
    ? { at: Math.min(...entries.map(e => e.fetchedAt)), stale: entries.some(e => e.stale) }
    : null;
  const failed = Object.values(rows).filter(e => e.reason === 'error').length;
  const retry = () => { setTickersFailed(false); setRetryNonce(n => n + 1); };

  return { rows, symbols, pending, meta, tickersFailed, failed, retry };
}

/* Single symbol, several intervals (MTF table). */
export function useSymbolRSI(symbol, intervals, retryNonce = 0) {
  const [rows, setRows] = useState({});
  const [tick, setTick] = useState(0);
  const key = intervals.join(',');
  useEffect(() => {
    const shortest = Math.min(...intervals.map(iv => TF[iv].ttl));
    const id = setInterval(() => setTick(t => t + 1), shortest);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (!symbol) { setRows({}); return; }
    let alive = true;
    const initial = {};
    for (const iv of intervals) { const c = peekRSI(symbol, iv); if (c) initial[iv] = c; }
    setRows(initial);
    intervals.forEach(iv => {
      fetchRSI(symbol, iv).then(e => { if (alive) setRows(prev => ({ ...prev, [iv]: e })); });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, key, tick, retryNonce]);
  return rows;
}
